import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState } from 'react';
import { X, DollarSign, User, Calendar, AlertCircle, CheckCircle, Clock, Info, RotateCcw, RefreshCw } from 'lucide-react';
import { vendorApi } from '../../services/api';
import Modal from '../ui/Modal';

interface RefundDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  refund: any;
  onRetrySuccess?: () => void;
}

const RefundDetailsModal: React.FC<RefundDetailsModalProps> = ({
  isOpen,
  onClose,
  refund,
  onRetrySuccess,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retrySuccess, setRetrySuccess] = useState(false);

  if (!refund) return null;

  // Check if refund can be retried
  const canRetry = refund.can_retry || (refund.status === 'failed' && (refund.retry_count || 0) < 3);

  const handleRetry = async () => {
    if (!canRetry) return;

    setIsRetrying(true);
    setRetryError(null);
    setRetrySuccess(false);

    try {
      await vendorApi.retryRefund(refund.id);
      setRetrySuccess(true);
      // Trigger refetch after a short delay
      setTimeout(() => {
        onRetrySuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      setRetryError(err.message || 'Failed to retry refund');
    } finally {
      setIsRetrying(false);
    }
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return `${CURRENCY_SYMBOL}0`;
    return `${CURRENCY_SYMBOL}${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Extract all fields from the enhanced API response
  const customerRefundAmount = refund.requested_amount || 0;
  const vendorShareOfRefund = refund.vendor_share_of_refund || refund.vendor_share || 0;
  const platformShareOfRefund = refund.platform_share_of_refund || 0;

  // Original booking amounts (now provided directly by API)
  const originalAmount = refund.original_booking_amount || 0;
  const originalVenueShare = refund.original_venue_share || 0;
  const originalPlatformCommission = refund.original_platform_commission || 0;
  const gatewayFee = refund.original_gateway_fee || 0;

  // Gateway fee responsibility (now provided directly by API)
  const venueGatewayFeeCost = refund.venue_gateway_fee_cost || 0;
  const customerGatewayFeeCost = refund.customer_gateway_fee_cost || 0;
  const refundGatewayFeePaidBy = refund.refund_gateway_fee_paid_by || 'venue';
  const gatewayFeeNote = refund.gateway_fee_note || null;

  // Total impact on vendor wallet
  const totalVendorDebt = refund.total_vendor_debt || (vendorShareOfRefund + venueGatewayFeeCost);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" animate={false}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Refund Details</h2>
              <p className="text-xs sm:text-sm text-gray-600">{refund.request_reference}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Timeline - Moved to Top */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-xl p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-600">Refund Requested</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">{formatDate(refund.requested_at)}</p>
                </div>
              </div>

              {refund.processed_at && (
                <>
                  <div className="hidden sm:block flex-1 mx-4">
                    <div className="h-0.5 bg-gradient-to-r from-blue-300 to-green-300"></div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    <div>
                      <p className="text-xs text-gray-600">Refund Processed</p>
                      <p className="text-xs sm:text-sm font-bold text-gray-900">{formatDate(refund.processed_at)}</p>
                    </div>
                  </div>
                </>
              )}

              {!refund.processed_at && (
                <>
                  <div className="hidden sm:block flex-1 mx-4">
                    <div className="h-0.5 bg-gray-200 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-300 to-transparent animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 animate-pulse" />
                    <div>
                      <p className="text-xs text-gray-600">Processing...</p>
                      <p className="text-xs sm:text-sm font-semibold text-blue-700">In Progress</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status Banner */}
          <div className={`rounded-xl p-3 sm:p-4 ${
            refund.status === 'processed' ? 'bg-green-50 border border-green-200' :
            refund.status === 'processing' ? 'bg-blue-50 border border-blue-200' :
            refund.status === 'failed' ? 'bg-red-50 border border-red-200' :
            'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-start gap-2 sm:gap-3">
              {refund.status === 'processed' ? (
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : refund.status === 'processing' ? (
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              ) : refund.status === 'failed' ? (
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm sm:text-base font-semibold ${
                  refund.status === 'processed' ? 'text-green-900' :
                  refund.status === 'processing' ? 'text-blue-900' :
                  refund.status === 'failed' ? 'text-red-900' :
                  'text-yellow-900'
                }`}>
                  {refund.status === 'processed' ? 'Refund Completed' :
                   refund.status === 'processing' ? 'Refund Processing' :
                   refund.status === 'failed' ? 'Refund Failed' :
                   'Refund Pending'}
                </p>
                <p className={`text-xs sm:text-sm mt-1 ${
                  refund.status === 'processed' ? 'text-green-700' :
                  refund.status === 'processing' ? 'text-blue-700' :
                  refund.status === 'failed' ? 'text-red-700' :
                  'text-yellow-700'
                }`}>
                  {refund.status === 'processed'
                    ? 'Customer has received their refund. Your balance has been adjusted.'
                    : refund.status === 'processing'
                    ? 'Refund is being processed by Paystack. This may take a few minutes.'
                    : refund.status === 'failed'
                    ? `Refund processing failed.${refund.failure_reason ? ` Reason: ${refund.failure_reason}` : ''}`
                    : 'Refund request is pending approval.'}
                </p>

                {/* Retry count info for failed refunds */}
                {refund.status === 'failed' && (
                  <p className="text-xs text-red-600 mt-1">
                    Retry attempts: {refund.retry_count || 0} of 3
                  </p>
                )}
              </div>
            </div>

            {/* Retry Section for Failed Refunds */}
            {refund.status === 'failed' && (
              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-red-200">
                {retrySuccess ? (
                  <div className="flex items-center gap-2 text-green-700 bg-green-100 rounded-lg p-2 sm:p-3">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-sm font-semibold">Retry initiated successfully! Refreshing...</span>
                  </div>
                ) : retryError ? (
                  <div className="flex items-center gap-2 text-red-700 bg-red-100 rounded-lg p-2 sm:p-3 mb-3">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm">{retryError}</span>
                  </div>
                ) : null}

                {canRetry && !retrySuccess && (
                  <button
                    onClick={handleRetry}
                    disabled={isRetrying}
                    className="flex items-center justify-center gap-2 w-full bg-red-600 text-white py-3 sm:py-2.5 rounded-xl sm:rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRetrying ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-4 h-4" />
                        Retry Refund (Attempt {(refund.retry_count || 0) + 1} of 3)
                      </>
                    )}
                  </button>
                )}

                {!canRetry && !retrySuccess && (
                  <div className="text-center text-red-600 text-xs sm:text-sm">
                    Maximum retry attempts reached. Please contact support.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Customer & Booking Info */}
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
            <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              Booking Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-600">Customer</p>
                <p className="text-sm font-semibold text-gray-900">{refund.customer_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Booking Reference</p>
                <p className="text-sm font-semibold text-gray-900 font-mono break-all">{refund.booking_reference}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Venue</p>
                <p className="text-sm font-semibold text-gray-900">{refund.venue_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Refund Requested</p>
                <p className="text-sm font-semibold text-gray-900">{formatDate(refund.requested_at)}</p>
              </div>
            </div>

            {/* Recurring Session Info - Show which session was refunded */}
            {refund.booking_type === 'recurring' && refund.gateway_response && (
              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Recurring Session Refund:</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 space-y-1">
                  {refund.gateway_response.instance_number && (
                    <p className="text-xs sm:text-sm text-blue-900">
                      <span className="font-semibold">Session {refund.gateway_response.instance_number}</span>
                      {refund.gateway_response.total_sessions && ` of ${refund.gateway_response.total_sessions}`}
                    </p>
                  )}
                  {refund.gateway_response.scheduled_date && (
                    <p className="text-xs text-blue-700">
                      Scheduled: {new Date(refund.gateway_response.scheduled_date).toLocaleDateString('en-NG', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  )}
                  {refund.gateway_response.session_value && (
                    <p className="text-xs text-blue-700">
                      Session Value: {formatCurrency(refund.gateway_response.session_value)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Original Booking Breakdown */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
            <h3 className="text-sm sm:text-base font-bold text-blue-900 mb-2 sm:mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
              Original Booking (When Customer Paid)
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm text-blue-700">Customer Paid:</span>
                <span className="text-base sm:text-lg font-bold text-blue-900">{formatCurrency(originalAmount)}</span>
              </div>
              <div className="h-px bg-blue-200"></div>
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm text-blue-700">→ Platform Commission:</span>
                <span className="text-xs sm:text-sm font-semibold text-blue-900">{formatCurrency(originalPlatformCommission)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm text-blue-700">→ Your Share:</span>
                <span className="text-xs sm:text-sm font-semibold text-blue-900">{formatCurrency(originalVenueShare)}</span>
              </div>
              {gatewayFee > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-blue-700">→ Gateway Fee (Paystack):</span>
                  <span className="text-xs sm:text-sm font-semibold text-blue-900">{formatCurrency(gatewayFee)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Refund Breakdown */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4">
            <h3 className="text-sm sm:text-base font-bold text-red-900 mb-2 sm:mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Refund Breakdown (Money Returned to Customer)</span>
              <span className="sm:hidden">Refund Breakdown</span>
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm text-red-700">Customer Received:</span>
                <span className="text-base sm:text-lg font-bold text-red-900">{formatCurrency(customerRefundAmount)}</span>
              </div>
              <div className="h-px bg-red-200"></div>
              <p className="text-xs text-red-700 italic">Split proportionally based on original booking:</p>
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm text-red-700">→ Platform refunded:</span>
                <span className="text-xs sm:text-sm font-semibold text-red-900">{formatCurrency(platformShareOfRefund)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm text-red-700">→ You refunded:</span>
                <span className="text-xs sm:text-sm font-semibold text-red-900">{formatCurrency(vendorShareOfRefund)}</span>
              </div>

              {/* Gateway Fee Info in Refund Breakdown - ALWAYS SHOW IF THERE WAS A GATEWAY FEE */}
              {gatewayFee > 0 && (
                <>
                  <div className="h-px bg-red-200 my-2"></div>
                  <div className="bg-red-100 rounded-lg p-2">
                    <p className="text-xs font-semibold text-red-800 mb-1">Gateway Fee (Non-Refundable):</p>
                    {venueGatewayFeeCost > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-red-700">→ You covered:</span>
                        <span className="text-xs font-bold text-red-900">{formatCurrency(venueGatewayFeeCost)}</span>
                      </div>
                    )}
                    {customerGatewayFeeCost > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-red-700">→ Customer covered:</span>
                        <span className="text-xs font-bold text-red-900">{formatCurrency(customerGatewayFeeCost)}</span>
                      </div>
                    )}
                    {!venueGatewayFeeCost && !customerGatewayFeeCost && gatewayFee > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-red-700">→ Platform covered:</span>
                        <span className="text-xs font-bold text-red-900">{formatCurrency(gatewayFee)}</span>
                      </div>
                    )}
                    <p className="text-xs text-red-600 italic mt-1">
                      Paystack keeps gateway fees even on refunds
                    </p>
                  </div>
                </>
              )}

              {refund.refund_percentage_from_policy && (
                <p className="text-xs text-red-600 mt-2">
                  ({refund.refund_percentage_from_policy}% refund per cancellation policy)
                </p>
              )}
            </div>
          </div>

          {/* Vendor Balance at Time of Refund */}
          {refund.vendor_balance_at_refund !== undefined && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
              <h3 className="text-sm sm:text-base font-bold text-blue-900 mb-2 sm:mb-3 flex items-center gap-2">
                <Info className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Your Available Balance (At Time of Refund)</span>
                <span className="sm:hidden">Available Balance</span>
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center bg-blue-100 rounded-lg p-2 sm:p-3">
                  <span className="text-xs sm:text-sm text-blue-700">Available for Payouts:</span>
                  <span className="text-base sm:text-lg font-bold text-blue-900">{formatCurrency(refund.vendor_balance_at_refund)}</span>
                </div>
                <p className="text-xs text-blue-700 italic">
                  This was your balance from completed bookings (held + eligible for payout) at the time the refund was requested.
                  {refund.covered_by_balance
                    ? ' ✓ Your balance covered this refund.'
                    : ' ⚠️ Your balance was insufficient - a debt was created.'}
                </p>
              </div>
            </div>
          )}

          {/* Total Impact on Your Wallet */}
          <div className="bg-gray-900 text-white rounded-xl p-3 sm:p-4">
            <h3 className="text-sm sm:text-base font-bold mb-2 sm:mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
              Total Deduction from Your Wallet
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm text-gray-300">Your refund share:</span>
                <span className="text-xs sm:text-sm font-semibold">{formatCurrency(vendorShareOfRefund)}</span>
              </div>
              <div className="h-px bg-gray-700"></div>
              <div className="flex justify-between items-center">
                <span className="text-sm sm:text-base font-bold">Total:</span>
                <span className="text-xl sm:text-2xl font-bold text-red-400">-{formatCurrency(vendorShareOfRefund)}</span>
              </div>
              {venueGatewayFeeCost > 0 && (
                <p className="text-xs text-gray-400 mt-2">
                  (Includes {CURRENCY_SYMBOL}{venueGatewayFeeCost.toLocaleString('en-NG', { minimumFractionDigits: 2 })} gateway fee that you absorbed)
                </p>
              )}
            </div>
          </div>

        </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
          <button
            onClick={onClose}
            className="w-full bg-gray-900 text-white py-3 sm:py-2.5 rounded-xl sm:rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
    </Modal>
  );
};

export default RefundDetailsModal;
